import React, { useRef, useState, useCallback, useEffect, useLayoutEffect, useMemo } from "react";

import { strings } from "@perceptron/constants";
import { EDITOR_CONSTANTS } from "@perceptron/constants/editorConstants";
import { Button } from "@perceptron/ui/components/button";
import { Icon } from "@perceptron/ui/components/icon";
import { cn } from "@perceptron/utils/cn";
import { formatString } from "@perceptron/utils/string";
import { MediaKind, MediaSetContext } from "@perceptron/utils/types";
import { Editor } from "@tiptap/react";

import { MAX_ROWS, SET_GAP, THUMB_MAX, THUMB_STAGGER_MS } from "@demo/constants/media-set-layout";
import { useIsMobile } from "@demo/hooks/useIsMobile";
import { usePrefersReducedMotion } from "@demo/hooks/usePrefersReducedMotion";
import { useTapHandlers } from "@demo/hooks/useTapHandlers";
import { MediaFactory } from "@demo/libs/media-factory";
import { MediaUploadService } from "@demo/libs/media-upload";
import { useMediaStore } from "@demo/store/media";
import { useModelStore } from "@demo/store/model";
import { useUploadProgress } from "@demo/store/upload-progress";
import { MediaData } from "@demo/types/base";
import { focusNextParagraphAfterMediaIfNeeded } from "@demo/utils/editor";
import { validateFiles } from "@demo/utils/file-handler";
import { orderMediaByIds } from "@demo/utils/media-order";
import { findMediaSet, resolveMediaSetDropPosition } from "@demo/utils/media-set-drag";
import { animateElementPositions, captureElementPositions, ElementPositions } from "@demo/utils/media-set-motion";

import { MediaPreviewModal } from "./Media/MediaPreviewModal";
import { MediaSetControls } from "./Media/MediaSetControls";

/** A tile that is not media: the "+N" that hides the rest, and its collapse. */
const overflowTile =
  "relative z-10 flex aspect-square w-full cursor-pointer items-center justify-center rounded-md " +
  "bg-background text-foreground-secondary transition-colors duration-150 " +
  "hover:bg-background-hover hover:text-foreground";

const DRAG_THRESHOLD = 5;
const MEDIA_TILE_DRAGGING_CLASS = "media-set-tile-dragging";

interface ActivePointerHandlers {
  move: (event: PointerEvent) => void;
  up: (event: PointerEvent) => void;
  cancel: (event: PointerEvent) => void;
}

interface DragPreviewSession {
  preview: HTMLElement | null;
  liftAnimation: Animation | null;
}

function setMediaTileDraggingCursor(isDragging: boolean) {
  if (typeof document === "undefined") return;
  document.body.classList.toggle(MEDIA_TILE_DRAGGING_CLASS, isDragging);
}

const EMPTY_MEDIA_IDS: string[] = [];

interface MediaSetPointerDragSession {
  pointerId: number;
  startX: number;
  startY: number;
  lastX: number;
  offsetX: number;
  offsetY: number;
  dropPos: number | null;
  origin: { width: number; height: number };
  started: boolean;
  preview: HTMLElement | null;
  liftAnimation: Animation | null;
}

interface MediaTilePointerDragSession {
  pointerId: number;
  mediaId: string;
  source: HTMLElement;
  startX: number;
  startY: number;
  lastX: number;
  offsetX: number;
  offsetY: number;
  size: { width: number; height: number };
  slots: Array<{ left: number; top: number; width: number; height: number }>;
  originOrder: string[];
  previewOrder: string[];
  started: boolean;
  preview: HTMLElement | null;
  liftAnimation: Animation | null;
}

function createDragPreview(source: HTMLElement, size: Pick<DOMRect, "width" | "height">, square = false) {
  const preview = source.cloneNode(true) as HTMLElement;
  preview.querySelectorAll("button, input, select, textarea").forEach((element) => element.remove());
  preview.querySelectorAll("video").forEach((video) => {
    const staticVideo = document.createElement("div");
    staticVideo.className = cn(video.className, "bg-cover bg-center");
    staticVideo.setAttribute("aria-hidden", "true");
    if (video.poster) {
      staticVideo.style.backgroundImage = `url("${video.poster.replaceAll('"', "%22")}")`;
    } else {
      staticVideo.classList.add("bg-muted");
    }
    video.replaceWith(staticVideo);
  });

  [preview, ...Array.from(preview.querySelectorAll<HTMLElement>("*"))].forEach((element) => {
    element.removeAttribute("id");
    element.removeAttribute("data-testid");
    element.removeAttribute("data-media-set-id");
    element.removeAttribute("data-drop-zone");
    element.removeAttribute("data-media-sort-item");
    element.removeAttribute("data-dragging");
  });
  preview.className = cn(
    "pointer-events-none fixed z-[10000] m-0 box-border origin-top-left rotate-0 select-none bg-background opacity-[.98] shadow-none outline outline-1 outline-[color-mix(in_srgb,var(--foreground)_10%,transparent)] transition-[rotate] duration-100 ease-out will-change-[left,top,opacity,scale,rotate]",
    square ? "overflow-hidden rounded-lg" : "overflow-visible rounded-xl",
  );

  preview.setAttribute("aria-hidden", "true");
  preview.setAttribute("inert", "");
  Object.assign(preview.style, {
    left: "0px",
    top: "0px",
    width: `${size.width}px`,
    height: `${square ? size.width : size.height}px`,
    aspectRatio: square ? "1 / 1" : undefined,
  });
  preview.style.setProperty("scale", "1.025");

  document.body.append(preview);
  return preview;
}

function positionDragPreview(
  session: Pick<MediaSetPointerDragSession, "preview" | "lastX" | "offsetX" | "offsetY">,
  clientX: number,
  clientY: number,
  reduceMotion: boolean,
) {
  const preview = session.preview;
  if (!preview) return;

  const tilt = reduceMotion ? 0 : Math.max(-0.45, Math.min(0.45, (clientX - session.lastX) * 0.08));
  preview.style.left = `${clientX - session.offsetX}px`;
  preview.style.top = `${clientY - session.offsetY}px`;
  preview.style.transformOrigin = `${session.offsetX}px ${session.offsetY}px`;
  preview.style.rotate = `${tilt}deg`;
  session.lastX = clientX;
}

function animateDragLift(preview: HTMLElement | null, reduceMotion: boolean) {
  if (!preview || reduceMotion) return null;
  if (typeof preview.animate !== "function") return null;
  return preview.animate(
    [
      { scale: "1.025", opacity: 0.9 },
      { scale: "1.035", opacity: 1 },
    ],
    { duration: 155, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
  );
}

function startDragLift(session: DragPreviewSession, reduceMotion: boolean) {
  const lift = animateDragLift(session.preview, reduceMotion);
  session.liftAnimation = lift;
  if (!lift) return;

  lift.onfinish = () => {
    if (!session.preview?.isConnected) return;
    lift.cancel();
    session.liftAnimation = null;
  };
}

function measureTileSlots(grid: HTMLElement) {
  return Array.from(grid.querySelectorAll<HTMLElement>("[data-media-sort-item]")).map((element) => ({
    left: element.offsetLeft,
    top: element.offsetTop,
    width: element.offsetWidth,
    height: element.offsetHeight,
  }));
}

function resolveTileDropTarget(
  grid: HTMLElement,
  slots: MediaTilePointerDragSession["slots"],
  clientX: number,
  clientY: number,
  columns: number,
) {
  if (slots.length === 0) return null;
  const rect = grid.getBoundingClientRect();
  const x = clientX - rect.left - grid.clientLeft;
  const y = clientY - rect.top - grid.clientTop;
  if (x < 0 || x > grid.clientWidth || y < 0 || y > grid.clientHeight) return null;

  let targetIndex = 0;
  let targetDistance = Number.POSITIVE_INFINITY;
  slots.forEach((slot, index) => {
    const distance = Math.hypot(x - (slot.left + slot.width / 2), y - (slot.top + slot.height / 2));
    if (distance < targetDistance) {
      targetDistance = distance;
      targetIndex = index;
    }
  });

  const slot = slots[targetIndex];
  if (!slot) return null;
  const vertical = columns <= 1;
  const after = vertical ? y > slot.top + slot.height / 2 : x > slot.left + slot.width / 2;
  return { index: targetIndex, after };
}

function settleDragPreview(
  preview: HTMLElement,
  editorDOM: HTMLElement,
  attribute: "data-media-set-id" | "data-media-sort-item",
  id: string,
  reduceMotion: boolean,
) {
  requestAnimationFrame(() => {
    if (!preview.isConnected) return;
    const destination = Array.from(editorDOM.querySelectorAll<HTMLElement>(`[${attribute}]`)).find(
      (element) => element.getAttribute(attribute) === id,
    );
    if (!destination) {
      preview.remove();
      return;
    }

    const from = preview.getBoundingClientRect();
    const to = destination.getBoundingClientRect();
    if (reduceMotion || typeof preview.animate !== "function") {
      preview.remove();
      return;
    }

    preview.style.left = `${from.left}px`;
    preview.style.top = `${from.top}px`;
    preview.style.removeProperty("transform-origin");
    preview.style.rotate = "0deg";
    preview.style.width = `${from.width}px`;
    preview.style.height = `${from.height}px`;
    const animation = preview.animate(
      [
        {
          left: `${from.left}px`,
          top: `${from.top}px`,
          width: `${from.width}px`,
          height: `${from.height}px`,
          scale: "1.025",
          opacity: 0.98,
        },
        {
          left: `${to.left}px`,
          top: `${to.top}px`,
          width: `${to.width}px`,
          height: `${to.height}px`,
          scale: "0.985",
          opacity: 0,
        },
      ],
      { duration: 190, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)", fill: "forwards" },
    );
    animation.onfinish = () => preview.remove();
  });
}

function moveMediaId(ids: string[], mediaId: string, targetId: string, afterTarget: boolean) {
  const sourceIndex = ids.indexOf(mediaId);
  const targetIndex = ids.indexOf(targetId);
  if (sourceIndex < 0 || targetIndex < 0) return ids;

  const nextIds = [...ids];
  const [draggedId] = nextIds.splice(sourceIndex, 1);
  if (!draggedId) return ids;

  const targetIndexAfterRemoval = nextIds.indexOf(targetId);
  if (targetIndexAfterRemoval < 0) return ids;
  nextIds.splice(targetIndexAfterRemoval + Number(afterTarget), 0, draggedId);
  return nextIds;
}

function sameOrder(left: string[], right: string[]) {
  return left.length === right.length && left.every((id, index) => id === right[index]);
}

function mergePreviewOrder(originOrder: string[], previewOrder: string[], currentOrder: string[]) {
  const originIds = new Set(originOrder);
  const availableIds = new Set(currentOrder);
  const desiredOrder = previewOrder.filter((id) => availableIds.has(id));
  let desiredIndex = 0;

  return currentOrder.map((id) => {
    if (!originIds.has(id)) return id;
    return desiredOrder[desiredIndex++] ?? id;
  });
}

const movedMediaSetExpansion = new WeakMap<Editor, Map<string, boolean>>();

function readMovedMediaSetExpansion(editor: Editor, mediaSetId: string) {
  return movedMediaSetExpansion.get(editor)?.get(mediaSetId) ?? false;
}

function preserveExpansionForMove(editor: Editor, mediaSetId: string, expanded: boolean) {
  const expansion = movedMediaSetExpansion.get(editor) ?? new Map<string, boolean>();
  expansion.set(mediaSetId, expanded);
  movedMediaSetExpansion.set(editor, expansion);
  requestAnimationFrame(() => {
    if (expansion.get(mediaSetId) === expanded) expansion.delete(mediaSetId);
  });
}

interface MediaSetProps {
  mediaSetId: string;
  mediaIds?: string[];
  editor: Editor;
  context?: MediaSetContext;
}

export function MediaSetCollection({
  mediaSetId,
  mediaIds = EMPTY_MEDIA_IDS,
  editor,
  context = MediaSetContext.DOCUMENT_EDITOR,
}: MediaSetProps) {
  const isMobile = useIsMobile();
  const reduceMotion = usePrefersReducedMotion();
  const canDrag = !isMobile && editor.isEditable;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(() => readMovedMediaSetExpansion(editor, mediaSetId));
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isDraggingSet, setIsDraggingSet] = useState(false);
  const mediaSetDragSession = useRef<MediaSetPointerDragSession | null>(null);
  const mediaTileDragSession = useRef<MediaTilePointerDragSession | null>(null);
  const [columns, setColumns] = useState(0);
  const [cell, setCell] = useState(THUMB_MAX);
  const [draggedMediaKey, setDraggedMediaKey] = useState<string | null>(null);
  const [previewMediaIds, setPreviewMediaIds] = useState<string[] | null>(null);
  const pendingTileLayout = useRef<ElementPositions | null>(null);
  const suppressTileClick = useRef<string | null>(null);
  const suppressTileClickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const windowPointerHandlers = useRef<ActivePointerHandlers | null>(null);
  const windowPointerCleanup = useRef<(() => void) | null>(null);

  const attachWindowPointerListeners = useCallback(() => {
    if (typeof window === "undefined" || windowPointerCleanup.current || !windowPointerHandlers.current) return;

    const move = (event: PointerEvent) => windowPointerHandlers.current?.move(event);
    const up = (event: PointerEvent) => windowPointerHandlers.current?.up(event);
    const cancel = (event: PointerEvent) => windowPointerHandlers.current?.cancel(event);
    window.addEventListener("pointermove", move, true);
    window.addEventListener("pointerup", up, true);
    window.addEventListener("pointercancel", cancel, true);
    windowPointerCleanup.current = () => {
      window.removeEventListener("pointermove", move, true);
      window.removeEventListener("pointerup", up, true);
      window.removeEventListener("pointercancel", cancel, true);
      windowPointerCleanup.current = null;
    };
  }, []);

  const detachWindowPointerListeners = useCallback(() => {
    windowPointerCleanup.current?.();
  }, []);

  // Subscribe to the mediaSets and derive media for this specific set
  // Find by ID only since the mediaSetId is unique
  const mediaSets = useMediaStore((state) => state.mediaSets);
  const matchingSet = useMemo(() => mediaSets.find((set) => set.id === mediaSetId), [mediaSets, mediaSetId]);
  const media = useMemo(() => {
    const storedMedia = matchingSet?.media ?? [];
    return orderMediaByIds(storedMedia, mediaIds);
  }, [matchingSet, mediaIds]);

  const displayedMedia = useMemo(() => {
    if (!previewMediaIds) return media;
    return orderMediaByIds(media, previewMediaIds);
  }, [media, previewMediaIds]);

  const captureTileLayout = useCallback(() => {
    return captureElementPositions(gridRef.current?.querySelectorAll<HTMLElement>("[data-media-sort-item]") ?? []);
  }, []);

  const animateTileLayout = useCallback((before: ElementPositions) => {
    animateElementPositions(before, { duration: 210, easing: "cubic-bezier(0.22, 1, 0.36, 1)" });
  }, []);

  // Get actions from the store (these don't need to be reactive)
  const removeMediaById = useMediaStore((state) => state.removeMediaById);

  const finishSetDrag = useCallback(
    (shouldMove: boolean) => {
      const session = mediaSetDragSession.current;
      if (!session) return;
      mediaSetDragSession.current = null;
      detachWindowPointerListeners();

      if (!session.started) {
        session.preview?.remove();
        return;
      }
      session.liftAnimation?.cancel();

      try {
        if (shouldMove) {
          preserveExpansionForMove(editor, mediaSetId, isExpanded);
          editor.commands.moveMediaSetContainer({ containerId: mediaSetId });
        }
      } catch (error) {
        console.error("Unable to move media set", error);
      } finally {
        if (!editor.isDestroyed) {
          try {
            editor.commands.clearMediaSetDrag();
          } catch (error) {
            console.error("Unable to clear media set drag state", error);
          }
        }
        setIsDraggingSet(false);
        if (session.preview) {
          if (editor.isDestroyed) session.preview.remove();
          else settleDragPreview(session.preview, editor.view.dom, "data-media-set-id", mediaSetId, reduceMotion);
        }
      }
    },
    [detachWindowPointerListeners, editor, isExpanded, mediaSetId, reduceMotion],
  );

  const handleSetPointerDown = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      if (!event.isPrimary || event.button !== 0 || mediaSetDragSession.current || mediaTileDragSession.current) return;
      const container = containerRef.current;
      if (!container) return;

      event.preventDefault();
      event.stopPropagation();
      const rect = container.getBoundingClientRect();
      mediaSetDragSession.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        dropPos: null,
        origin: { width: rect.width, height: rect.height },
        started: false,
        preview: null,
        liftAnimation: null,
      };
      attachWindowPointerListeners();
      event.currentTarget.setPointerCapture(event.pointerId);
    },
    [attachWindowPointerListeners],
  );

  const handleSetPointerMove = useCallback(
    (event: Pick<PointerEvent, "pointerId" | "clientX" | "clientY" | "preventDefault" | "stopPropagation">) => {
      const session = mediaSetDragSession.current;
      if (!session || session.pointerId !== event.pointerId) return;

      if (!session.started) {
        const distance = Math.hypot(event.clientX - session.startX, event.clientY - session.startY);
        if (distance < DRAG_THRESHOLD) return;

        if (!editor.commands.startMediaSetDrag({ containerId: mediaSetId, slotHeight: session.origin.height })) {
          mediaSetDragSession.current = null;
          detachWindowPointerListeners();
          return;
        }

        session.started = true;
        const container = containerRef.current;
        if (container) session.preview = createDragPreview(container, session.origin);
        setIsDraggingSet(true);
        startDragLift(session, reduceMotion);
      }

      event.preventDefault();
      event.stopPropagation();
      positionDragPreview(session, event.clientX, event.clientY, reduceMotion);
      const dropPos = resolveMediaSetDropPosition(editor.view, mediaSetId, event.clientX, event.clientY);
      if (dropPos !== undefined && dropPos !== session.dropPos) {
        session.dropPos = dropPos;
        editor.commands.updateMediaSetDropTarget({ containerId: mediaSetId, dropPos });
      }
    },
    [detachWindowPointerListeners, editor, mediaSetId, reduceMotion],
  );

  const handleSetPointerCancel = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      const session = mediaSetDragSession.current;
      if (!session || session.pointerId !== event.pointerId) return;
      finishSetDrag(false);
    },
    [finishSetDrag],
  );

  useEffect(
    () => () => {
      detachWindowPointerListeners();
      const session = mediaSetDragSession.current;
      mediaSetDragSession.current = null;
      session?.liftAnimation?.cancel();
      session?.preview?.remove();
      if (session?.started && !editor.isDestroyed) editor.commands.clearMediaSetDrag();

      const tileSession = mediaTileDragSession.current;
      mediaTileDragSession.current = null;
      setMediaTileDraggingCursor(false);
      tileSession?.liftAnimation?.cancel();
      tileSession?.preview?.remove();
      if (suppressTileClickTimer.current) clearTimeout(suppressTileClickTimer.current);
    },
    [detachWindowPointerListeners, editor],
  );

  useEffect(() => {
    if (
      previewMediaIds &&
      sameOrder(
        media.map((item) => item.key),
        previewMediaIds,
      )
    ) {
      setPreviewMediaIds(null);
    }
  }, [media, previewMediaIds]);

  // Subscribe to upload progress for only this specific media set
  const uploadProgress = useUploadProgress(mediaSetId);
  const isUploading = uploadProgress?.isUploading ?? false;
  const uploadedFiles = uploadProgress?.uploadedFiles ?? 0;
  const totalFiles = uploadProgress?.totalFiles ?? 0;
  const pendingPreviews = uploadProgress?.pendingPreviews ?? [];

  // Get the model's media capabilities to filter file picker options
  const mediaCapabilities = useModelStore(
    (state) => state.selectedModel?.capabilities?.media ?? [MediaKind.Image, MediaKind.Video],
  );
  const acceptedMimeTypes = MediaFactory.getAcceptedMimeTypesForCapabilities(mediaCapabilities).join(",");

  const handleMediaClick = useCallback((e: React.MouseEvent | React.TouchEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    setCurrentMediaIndex(index);
    setIsModalOpen(true);
  }, []);

  const updateTileDropTarget = useCallback(
    (session: MediaTilePointerDragSession, clientX: number, clientY: number) => {
      const grid = gridRef.current;
      const resolved = grid ? resolveTileDropTarget(grid, session.slots, clientX, clientY, columns) : null;
      if (!resolved) return null;

      const targetId = session.originOrder[resolved.index];
      if (!targetId) return null;
      const nextOrder = moveMediaId(session.originOrder, session.mediaId, targetId, resolved.after);
      const previewChanged = !sameOrder(session.previewOrder, nextOrder);
      if (previewChanged) {
        pendingTileLayout.current = captureTileLayout();
        session.previewOrder = nextOrder;
        setPreviewMediaIds(nextOrder);
      }

      return { order: nextOrder };
    },
    [captureTileLayout, columns],
  );

  const finishMediaTileDrag = useCallback(
    (shouldCommit: boolean) => {
      const session = mediaTileDragSession.current;
      if (!session) return;
      mediaTileDragSession.current = null;
      detachWindowPointerListeners();
      setMediaTileDraggingCursor(false);

      if (!session.started) {
        session.preview?.remove();
        return;
      }
      session.liftAnimation?.cancel();

      let committedOrder: string[] | null = null;
      try {
        const currentSet = useMediaStore.getState().mediaSets.find((set) => set.id === mediaSetId);
        const currentNode = editor.isDestroyed
          ? undefined
          : findMediaSet(editor.state.doc, EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER, mediaSetId);
        const currentMediaIds = Array.isArray(currentNode?.node.attrs.mediaIds)
          ? currentNode.node.attrs.mediaIds
          : media.map((item) => item.key);
        const currentOrder = orderMediaByIds(currentSet?.media ?? media, currentMediaIds).map((item) => item.key);
        const intendedOrder = shouldCommit
          ? mergePreviewOrder(session.originOrder, session.previewOrder, currentOrder)
          : null;
        if (intendedOrder && !sameOrder(currentOrder, intendedOrder) && !editor.isDestroyed) {
          pendingTileLayout.current = captureTileLayout();
          if (editor.commands.reorderMediaInSet({ containerId: mediaSetId, mediaIds: intendedOrder })) {
            committedOrder = intendedOrder;
          }
        }
      } catch (error) {
        console.error("Unable to reorder media", error);
      } finally {
        if (!committedOrder) pendingTileLayout.current = null;
        setDraggedMediaKey(null);
        setPreviewMediaIds(committedOrder);

        if (shouldCommit) {
          if (suppressTileClickTimer.current) clearTimeout(suppressTileClickTimer.current);
          suppressTileClick.current = session.mediaId;
          suppressTileClickTimer.current = setTimeout(() => {
            suppressTileClick.current = null;
            suppressTileClickTimer.current = null;
          }, 300);
        }

        if (session.preview) {
          if (editor.isDestroyed) session.preview.remove();
          else
            settleDragPreview(session.preview, editor.view.dom, "data-media-sort-item", session.mediaId, reduceMotion);
        }
      }
    },
    [captureTileLayout, detachWindowPointerListeners, editor, media, mediaSetId, reduceMotion],
  );

  const handleMediaPointerDown = useCallback(
    (event: React.PointerEvent<HTMLDivElement>, mediaId: string) => {
      if (
        !canDrag ||
        !event.isPrimary ||
        event.button !== 0 ||
        mediaSetDragSession.current ||
        mediaTileDragSession.current
      ) {
        return;
      }
      if (event.target instanceof Element && event.target.closest("button, input, select, textarea, a")) return;

      const grid = gridRef.current;
      if (!grid) return;
      const rect = event.currentTarget.getBoundingClientRect();
      const edge = Math.min(event.currentTarget.offsetWidth, event.currentTarget.offsetHeight);
      const currentOrder = media.map((item) => item.key);
      if (!currentOrder.includes(mediaId)) return;

      event.stopPropagation();
      mediaTileDragSession.current = {
        pointerId: event.pointerId,
        mediaId,
        source: event.currentTarget,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        offsetX: rect.width ? ((event.clientX - rect.left) * edge) / rect.width : edge / 2,
        offsetY: rect.height ? ((event.clientY - rect.top) * edge) / rect.height : edge / 2,
        size: { width: edge, height: edge },
        slots: measureTileSlots(grid),
        originOrder: currentOrder,
        previewOrder: currentOrder,
        started: false,
        preview: null,
        liftAnimation: null,
      };
      attachWindowPointerListeners();
    },
    [attachWindowPointerListeners, canDrag, media],
  );

  const handleMediaPointerMove = useCallback(
    (event: Pick<PointerEvent, "pointerId" | "clientX" | "clientY" | "preventDefault" | "stopPropagation">) => {
      const session = mediaTileDragSession.current;
      if (!session || session.pointerId !== event.pointerId) return;

      if (!session.started) {
        const distance = Math.hypot(event.clientX - session.startX, event.clientY - session.startY);
        if (distance < DRAG_THRESHOLD) return;

        session.started = true;
        setMediaTileDraggingCursor(true);
        session.preview = createDragPreview(session.source, session.size, true);
        startDragLift(session, reduceMotion);
        setDraggedMediaKey(session.mediaId);
        gridRef.current?.setPointerCapture(event.pointerId);
      }

      event.preventDefault();
      event.stopPropagation();
      positionDragPreview(session, event.clientX, event.clientY, reduceMotion);
      updateTileDropTarget(session, event.clientX, event.clientY);
    },
    [reduceMotion, updateTileDropTarget],
  );

  const handleMediaPointerCancel = useCallback(
    (event: React.PointerEvent<HTMLDivElement> | PointerEvent) => {
      const session = mediaTileDragSession.current;
      if (!session || session.pointerId !== event.pointerId) return;
      finishMediaTileDrag(false);
    },
    [finishMediaTileDrag],
  );

  const handleMediaClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>, mediaId: string) => {
    if (suppressTileClick.current !== mediaId) return;
    event.preventDefault();
    event.stopPropagation();
    suppressTileClick.current = null;
    if (suppressTileClickTimer.current) clearTimeout(suppressTileClickTimer.current);
    suppressTileClickTimer.current = null;
  }, []);

  windowPointerHandlers.current = {
    move: (event) => {
      if (mediaTileDragSession.current?.pointerId === event.pointerId) handleMediaPointerMove(event);
      else if (mediaSetDragSession.current?.pointerId === event.pointerId) handleSetPointerMove(event);
    },
    up: (event) => {
      const tileSession = mediaTileDragSession.current;
      if (tileSession?.pointerId === event.pointerId) {
        if (tileSession.started) event.preventDefault();
        finishMediaTileDrag(true);
        return;
      }

      const setSession = mediaSetDragSession.current;
      if (!setSession || setSession.pointerId !== event.pointerId) return;
      if (setSession.started) event.preventDefault();
      finishSetDrag(true);
    },
    cancel: (event) => {
      const tileSession = mediaTileDragSession.current;
      if (tileSession?.pointerId === event.pointerId) {
        finishMediaTileDrag(false);
        return;
      }

      const setSession = mediaSetDragSession.current;
      if (setSession?.pointerId === event.pointerId) finishSetDrag(false);
    },
  };

  const handleChangeMediaIndex = useCallback((newIndex: number) => {
    setCurrentMediaIndex(newIndex);
  }, []);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteMedia = useCallback(
    (e: React.MouseEvent | React.TouchEvent, index: number) => {
      e.preventDefault();
      e.stopPropagation();
      const mediaItem = displayedMedia[index];
      if (!mediaItem) return;
      if (!media.some((item) => item.key === mediaItem.key)) return;
      removeMediaById(mediaSetId, mediaItem.key);

      const isLastMedia = media.length === 1;
      let mediaSetCount = 0;
      editor.state.doc.descendants((node) => {
        if (node.type.name === EDITOR_CONSTANTS.NODE_TYPES.MEDIA_SET_CONTAINER) mediaSetCount += 1;
      });

      if (isLastMedia && mediaSetCount > 1 && !isUploading) {
        editor.commands.deleteMediaSetContainer(mediaSetId);
      } else {
        editor.commands.removeMediaFromSet({
          containerId: mediaSetId,
          mediaId: mediaItem.key,
        });
      }
      resetFileInput();
    },
    [mediaSetId, displayedMedia, media, editor, isUploading, removeMediaById],
  );

  const handleAddMedia = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      e.preventDefault();
      e.stopPropagation();

      // Ensure we blur the editor before opening file picker
      if (editor && editor.view) {
        editor.view.dom.blur();
      }

      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    },
    [editor],
  );

  const handleExpandClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsExpanded((expanded) => !expanded);
  };

  const handleRemoveMediaSet = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // The editor extension moves this draft set into an undo tombstone when
    // the node leaves the document, then restores it if history brings it back.
    if (editor) {
      editor.commands.deleteMediaSetContainer(mediaSetId);
    }
  };

  // Bundled so a scroll that starts on any of these does not fire them.
  const expandTap = useTapHandlers((event) => handleExpandClick(event));
  const addMediaTap = useTapHandlers((event) => handleAddMedia(event));
  const removeSetTap = useTapHandlers((event) => handleRemoveMediaSet(event));

  const handleMediaUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const filesArray = Array.from(files);

    try {
      // Validate files against model capabilities (shows toast errors for unsupported types)
      const { validatedImageFiles, validatedVideoFiles } = await validateFiles(filesArray);
      const allValidFiles = [...validatedImageFiles, ...validatedVideoFiles];

      if (allValidFiles.length === 0) {
        return;
      }

      await MediaUploadService.processMediaFiles(allValidFiles, mediaSetId, editor);
    } catch (error) {
      console.error("Error uploading media files:", error);
    } finally {
      resetFileInput();

      // Focus the next textbox after upload is complete
      if (editor) {
        focusNextParagraphAfterMediaIfNeeded(editor, mediaSetId);
      }
    }
  };

  // Column count is chosen here rather than by `auto-fill`, so the tracks can
  // be 1fr — no leftover space at the right — while still never exceeding
  // THUMB_MAX: the fewest columns whose cells fit under the cap. `auto-fill
  // minmax(min,max)` leaves a gap; `minmax(min,1fr)` overshoots the cap at
  // three columns or fewer. The same count tells the add button whether it has
  // a thumbnail to tuck behind.
  const hasTiles = displayedMedia.length > 0 || pendingPreviews.length > 0;
  useLayoutEffect(() => {
    const node = gridRef.current;
    if (!node) return;
    const measure = (width: number) => {
      if (width <= 0) return;
      const count = Math.max(1, Math.ceil((width + SET_GAP) / (THUMB_MAX + SET_GAP)));
      setColumns(count);
      setCell((width - SET_GAP * (count - 1)) / count);
    };
    // Synchronously, before the browser paints: dropping fifteen files would
    // otherwise show all fifteen for a frame and then collapse to two rows.
    measure(node.getBoundingClientRect().width);
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(([entry]) => measure(entry?.contentRect.width ?? 0));
    observer.observe(node);
    return () => observer.disconnect();
    // The grid only exists once there is something to put in it, and media
    // arrives after the first render — so this has to re-attach when the grid
    // appears rather than run once against a ref that is still null.
  }, [hasTiles]);

  // Until a width arrives there is no honest column count, and guessing one
  // collapses the set behind a "+N" for a frame — or for good, in an
  // environment with no ResizeObserver at all. Unmeasured means "show
  // everything" rather than "assume one column".
  const measured = columns > 0;
  // Uploads in flight take cells like anything else. Counting only the media
  // let a drop of twenty files render twenty previews, so the set stood at six
  // rows until they landed and then snapped back to two.
  const capacity = columns * MAX_ROWS;
  const items = media.length + pendingPreviews.length;
  const overflowing = measured && items > capacity;
  const collapsed = overflowing && !isExpanded;
  // Cells for real items; collapsed, the last one goes to the "+N".
  const slots = collapsed ? Math.max(0, capacity - 1) : items;
  const visibleMedia = displayedMedia.slice(0, Math.min(displayedMedia.length, slots));
  const visiblePreviews = pendingPreviews.slice(0, Math.max(0, slots - visibleMedia.length));
  const hiddenCount = items - visibleMedia.length - visiblePreviews.length;
  // Cells occupied, counting the "+N" or collapse tile: a partial last row has
  // a cell the add square can tuck into.
  const cells = visibleMedia.length + visiblePreviews.length + (overflowing ? 1 : 0);
  const hasFreeCell = measured && cells > 0 && cells % columns !== 0;
  const rows = measured ? Math.max(1, Math.ceil(cells / columns)) : 1;
  // On expand, only the thumbnails that were hidden animate in. Everything
  // already on screen stays put.
  const revealFrom = overflowing && isExpanded ? Math.min(media.length, Math.max(0, capacity - 1)) : Infinity;
  const visibleMediaOrderKey = visibleMedia.map((item) => item.key).join("\u0000");

  useLayoutEffect(() => {
    const before = pendingTileLayout.current;
    if (!before) return;
    pendingTileLayout.current = null;
    animateTileLayout(before);
  }, [animateTileLayout, previewMediaIds, visibleMediaOrderKey]);

  const isInVariableEditor = context === MediaSetContext.VARIABLE_EDITOR;
  // A phone shows one set and nothing that would rebuild it, so discarding it
  // is a dead end rather than an action. The empty state's own remove has
  // always been desktop-only for the same reason.
  const canRemoveSet = !isInVariableEditor && !isMobile;

  return (
    <div
      className={cn(
        "group/set relative mb-2 select-none transition-[opacity,transform,box-shadow] duration-150 ease-out motion-reduce:transition-none",
        isDraggingSet &&
          "outline-foreground-secondary/40 z-20 scale-[0.99] opacity-25 shadow-none outline outline-dashed outline-1",
        // The tan card is the empty state's own surface. Once there are tiles
        // the set is transparent: the tiles are that same tan, and they only
        // read as tiles against the editor's white card behind them.
        !hasTiles && "cursor-pointer rounded-xl bg-background px-3 py-2 sm:px-2",
      )}
      data-dragging={isDraggingSet || undefined}
      data-drop-zone="media-set-collection"
      data-media-set-id={mediaSetId}
      data-testid="media-set-collection"
      ref={containerRef}
    >
      {/* Only the empty card can be discarded. Once there are thumbnails,
          removing them one at a time is the way out — sets are small, and a
          control that throws all of them away sat on the grid competing with
          each thumbnail's own remove for the same click. */}
      {canRemoveSet && !hasTiles && (
        <button
          aria-label={strings.mediaSetCollection.removeSet}
          className="absolute right-2 top-2 z-30 flex h-6 w-6 transform cursor-pointer items-center justify-center rounded-full bg-foreground opacity-0 transition-opacity will-change-[opacity,transform] hover:bg-foreground focus-visible:opacity-100 group-hover/set:opacity-100 dark:bg-background-alt-2"
          {...removeSetTap}
        >
          <Icon className="text-white" name="handle-remove" />
        </button>
      )}
      {canDrag && (
        <button
          aria-label={strings.mediaSetCollection.moveSet}
          className="pointer-events-none absolute left-2 top-2 z-40 flex size-7 cursor-grab items-center justify-center rounded-md bg-background-alt text-foreground-secondary opacity-0 shadow-sm transition-[opacity,transform,color,background-color,box-shadow] duration-150 ease-out hover:scale-105 hover:bg-background-hover hover:text-foreground focus-visible:pointer-events-auto focus-visible:opacity-100 active:scale-95 active:cursor-grabbing group-focus-within/set:pointer-events-auto group-hover/set:pointer-events-auto group-hover/set:opacity-100 motion-reduce:transition-none"
          title={strings.mediaSetCollection.moveSet}
          type="button"
          onLostPointerCapture={handleSetPointerCancel}
          onPointerCancel={handleSetPointerCancel}
          onPointerDown={handleSetPointerDown}
        >
          <Icon className="size-4" name="grip-vertical" />
        </button>
      )}
      {isUploading && (
        // Above the tiles, not merely equal to them: this is rendered before
        // the grid, so at a matching z-index they paint over it.
        <div className="absolute bottom-2 right-2 z-40 rounded-md bg-white/90 px-2 py-1 text-xs text-gray-700 shadow-sm">
          {formatString(strings.mediaSetCollection.uploadProgress, uploadedFiles, totalFiles, "file")}
        </div>
      )}
      <input
        accept={acceptedMimeTypes}
        className="hidden"
        data-set-id={mediaSetId}
        data-testid="media-set-file-input"
        multiple
        ref={fileInputRef}
        type="file"
        onChange={handleMediaUpload}
      />
      {media.length === 0 && !isUploading && (
        <div
          className="flex w-full cursor-pointer flex-col items-center justify-center gap-4 p-6"
          data-testid="add-media-button-empty"
          {...addMediaTap}
        >
          <div className="pointer-events-none relative h-[48px] w-[48px]">
            <Icon
              alt={strings.mediaSetCollection.pictureIconBackground}
              className="absolute bottom-0 left-0.5 size-11 fill-background transition-transform duration-300 group-hover/set:translate-x-[-8px] group-hover/set:translate-y-[-4px] group-hover/set:rotate-[-20deg]"
              name="video"
            />
            <Icon
              alt={strings.mediaSetCollection.pictureIconForeground}
              className="absolute size-12 fill-background transition-transform duration-300 group-hover/set:-translate-y-1 group-hover/set:translate-x-2 group-hover/set:rotate-[20deg]"
              name="album-image"
            />
          </div>
          <div className="pointer-events-none flex flex-col items-center">
            <span className="text-md">{formatString(strings.mediaSetCollection.uploadMedia, "media")}</span>
          </div>
        </div>
      )}
      {hasTiles && (
        <div
          className="relative grid w-full shrink-0 items-center"
          ref={gridRef}
          style={{
            gap: SET_GAP,
            // Equal tracks fill the row edge to edge; the count above is what
            // keeps each one under THUMB_MAX. Cells are square via
            // aspect-ratio, so rows stay aligned as the width changes.
            gridTemplateColumns: measured ? `repeat(${columns}, minmax(0, 1fr))` : undefined,
            // Cell size, for anything that needs to align to a row.
            ["--row" as string]: `${cell}px`,
          }}
          onDragStart={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onLostPointerCapture={handleMediaPointerCancel}
          onPointerCancel={handleMediaPointerCancel}
        >
          {visibleMedia.map((mediaItem: MediaData, index: number) => {
            const itemMediaKind = MediaFactory.detectMediaKind(mediaItem);
            const MediaRenderer = MediaFactory.getRenderer(itemMediaKind);

            return (
              <div
                className={cn(
                  "relative z-10 aspect-square w-full transition-[opacity,transform,box-shadow] duration-150 ease-out motion-reduce:transition-none",
                  canDrag && "cursor-grab active:cursor-grabbing",
                  index >= revealFrom && "thumb-enter",
                  draggedMediaKey === mediaItem.key &&
                    "outline-foreground-secondary/40 scale-[0.97] opacity-25 outline outline-dashed outline-1",
                )}
                data-media-sort-item={mediaItem.key}
                draggable={false}
                key={mediaItem.key}
                style={
                  index >= revealFrom ? { animationDelay: `${(index - revealFrom) * THUMB_STAGGER_MS}ms` } : undefined
                }
                title={canDrag ? formatString(strings.mediaSetCollection.dragMediaToReorder, index + 1) : undefined}
                onClickCapture={(event) => handleMediaClickCapture(event, mediaItem.key)}
                onDragStart={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                onPointerDown={(event) => handleMediaPointerDown(event, mediaItem.key)}
              >
                <MediaRenderer
                  index={index}
                  loading={index < MAX_ROWS * Math.max(columns, 1) ? "eager" : "lazy"}
                  media={mediaItem}
                  mediaKind={itemMediaKind}
                  mediaSetId={mediaSetId}
                  showControls
                  onRemove={handleDeleteMedia}
                  onSelect={handleMediaClick}
                />
              </div>
            );
          })}
          {isUploading &&
            visiblePreviews.map((preview, i) => {
              const fraction = preview.bytesTotal > 0 ? Math.min(1, preview.bytesLoaded / preview.bytesTotal) : 0;
              // Until the first byte event lands we have no real fraction; pulse
              // the empty track to communicate "received, waiting". Once bytes
              // flow the pulse stops and the same bar fills determinately. Once
              // this file's PUT resolves, drop the blur + bar so it stops looking
              // in-progress while siblings finish.
              const isIndeterminate = !preview.done && preview.bytesLoaded === 0;
              return (
                <div
                  className="relative z-10 flex aspect-square w-full items-center justify-center overflow-hidden rounded-md bg-secondary shadow-[0_4px_4px_rgba(0,0,0,0.07)]"
                  data-testid="media-upload-pending"
                  key={`pending-${i}`}
                >
                  {preview.kind === "video" ? (
                    <video
                      aria-hidden="true"
                      className="h-full w-full object-cover"
                      muted
                      playsInline
                      preload="metadata"
                      src={preview.url}
                      style={{ filter: preview.done ? undefined : "blur(5px) saturate(1.3)" }}
                    />
                  ) : (
                    // Blob URL from a freshly-selected File — next/image's optimizer
                    // can't handle blob: sources, so use a plain <img>.
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      alt=""
                      className="h-full w-full object-cover"
                      src={preview.url}
                      style={{ filter: preview.done ? undefined : "blur(5px) saturate(1.3)" }}
                    />
                  )}
                  {!preview.done && (
                    // Force dark-theme tokens so the bar reads consistently on
                    // either app theme (the `primary` fill is near-white in dark
                    // mode and dark-grey in light mode — locking dark keeps the
                    // bar visible against the blurred preview underneath).
                    <div
                      className={cn(
                        "dark absolute inset-x-2 bottom-2 h-1.5 overflow-hidden rounded-full bg-black/40",
                        isIndeterminate && "animate-pulse",
                      )}
                    >
                      <div
                        className="h-full rounded-full bg-primary transition-[width] duration-300 ease-out"
                        data-testid="media-upload-progress-bar"
                        style={{ width: `${fraction * 100}%` }}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          {collapsed && (
            <button
              aria-label={formatString(strings.mediaSetCollection.showMore, hiddenCount)}
              className={overflowTile}
              type="button"
              {...expandTap}
            >
              <span className="font-medium title-3">+{hiddenCount}</span>
            </button>
          )}
          {overflowing && isExpanded && (
            <button
              aria-label={strings.mediaSetCollection.collapse}
              className={overflowTile}
              type="button"
              {...expandTap}
            >
              <Icon className="size-[22px]" name="chevrons-down-up" />
            </button>
          )}
          {!isUploading && (
            <MediaSetControls hasFreeCell={hasFreeCell} isMobile={isMobile} rows={rows} onAdd={handleAddMedia} />
          )}
        </div>
      )}
      {hasTiles && isMobile && !isUploading && (
        <Button
          className="mt-2 w-full"
          data-testid="add-media-button-mobile"
          type="button"
          variant="outline"
          {...addMediaTap}
        >
          {strings.mediaSetCollection.addMoreMedia}
        </Button>
      )}
      {media.length > 0 && (
        <>
          {isModalOpen && media[currentMediaIndex] && (
            <MediaPreviewModal
              currentMediaIndex={currentMediaIndex}
              isOpen={isModalOpen}
              media={media[currentMediaIndex]}
              mediaKind={MediaFactory.detectMediaKind(media[currentMediaIndex])}
              mediaSetId={mediaSetId}
              onChangeMediaIndex={handleChangeMediaIndex}
              onClose={() => setIsModalOpen(false)}
            />
          )}
        </>
      )}
    </div>
  );
}
